/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { InteractivityChecker } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  numberAttribute,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { isEscapeKey, isNotEmpty, isNotNull } from '@common/tech-shared';
import { debounce, DebouncedFunc, isEmpty, uniqueId } from 'lodash-es';
import { FocusUtil } from '../focus.util';
import { TooltipComponent } from './tooltip.component';
import { TooltipService } from './tooltip.service';

export enum TooltipPosition {
  ABOVE = 'above',
  BELOW = 'below',
}

export const REM = 16;
export const MAX_WIDTH = 24 * REM;
export const VIEWPORT_MARGIN = 1 * REM;

type TooltipAriaType = 'aria-describedby' | 'aria-label';

@Directive({
  selector: '[tooltip]',
})
export class TooltipDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() set tooltip(value: string) {
    if (isNotNull(this.tooltipId) && isNotNull(this.parentElement)) {
      this._removeAriaAttribute(this.tooltipAriaType);
    }

    this.tooltipText = value;
    this.tooltipId = uniqueId('tooltip-');
    if (isNotNull(this.parentElement) && isNotEmpty(value)) {
      this._setAriaAttribute(this.tooltipAriaType);
    }
    if (this.tooltipService.activeOwner === this._ownerToken) {
      this._updateVisibleTooltip(value);
    }
  }

  @Input() tooltipPosition: TooltipPosition = TooltipPosition.BELOW;
  @Input({ transform: numberAttribute }) tooltipDelay: number = 700;
  @Input() tooltipAriaType: TooltipAriaType = 'aria-describedby';

  tooltipText: string = null;
  tooltipDataTestId: string = null;
  parentElement: HTMLElement = null;
  tooltipId: string = null;
  position: TooltipPosition;
  leftOffset: number = 0;
  debouncedShowTooltip: DebouncedFunc<() => void>;

  private readonly _ownerToken: symbol = Symbol('tooltip-directive');

  private readonly tooltipService: TooltipService = inject(TooltipService);
  public readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  public readonly renderer: Renderer2 = inject(Renderer2);
  public readonly interactivityChecker: InteractivityChecker = inject(InteractivityChecker);

  dataTestIdParentAttribute: string = 'data-test-id';
  dataTestIdTooltipSuffix: string = '-tooltip';

  ngOnInit(): void {
    this.debouncedShowTooltip = debounce(() => this._show(), this.tooltipDelay);
    this.parentElement = this._getParentElement(this.elementRef.nativeElement);
    this._addDataTestId();
  }

  ngAfterViewInit(): void {
    if (isNotNull(this.tooltipId) && isNotNull(this.parentElement) && isNotEmpty(this.tooltipText)) {
      this._setAriaAttribute(this.tooltipAriaType);
    }
  }

  ngOnDestroy(): void {
    this._destroy();
  }

  @HostListener('mouseenter')
  _showTooltipOnHover(): void {
    if (isEmpty(this.tooltipText)) return;

    this.debouncedShowTooltip();
  }

  @HostListener('focusin')
  _showTooltipOnFocus(): void {
    if (isEmpty(this.tooltipText)) return;

    this._show();
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  _hideTooltip(): void {
    this.debouncedShowTooltip.cancel();
    this._hide();
  }

  @HostListener('keydown', ['$event'])
  _onKeydown(e: KeyboardEvent): void {
    if (isEscapeKey(e)) {
      this._hide();
    }
  }

  _show(): void {
    if (isEmpty(this.tooltipText)) return;

    const instance = this.tooltipService.tooltipComponentRef.instance;
    instance.text = this.tooltipText;
    instance.id = this.tooltipId;
    instance.dataTestId = this.tooltipDataTestId;

    this.tooltipService.setActiveOwner(this._ownerToken);
    this.tooltipService.mount(this._ownerToken);
    this._setWidth();
    setTimeout(() => {
      this._setTooltipProperties();
      this.tooltipService.show(this._ownerToken);
    });
  }

  _updateVisibleTooltip(value: string): void {
    this.tooltipService.tooltipComponentRef.instance.text = value;
    this._setWidth();
    setTimeout(() => this._setTooltipProperties());
  }

  _addDataTestId(): void {
    const dataTestId: string = this.parentElement?.getAttribute(this.dataTestIdParentAttribute);
    if (isNotNull(dataTestId)) this.tooltipDataTestId = dataTestId + this.dataTestIdTooltipSuffix;
  }

  _setTooltipProperties(): void {
    const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();
    this._setTooltipOffsetAndPosition();

    const instance = this.tooltipService.tooltipComponentRef.instance;
    instance.left = (right + left) / 2 + this.leftOffset;
    instance.top = this._getTopPosition(top, bottom, this.position);
    instance.position = this.position;
    instance.leftOffset = this.leftOffset;
  }

  _setWidth(): void {
    const componentRef: ComponentRef<TooltipComponent> = this.tooltipService.tooltipComponentRef;
    componentRef.instance.width = null;
    componentRef.changeDetectorRef.detectChanges();

    const currentWidth: number = componentRef.location.nativeElement.children[0].getBoundingClientRect().width;
    const maxWidthPx: number = Math.min(MAX_WIDTH, document.documentElement.clientWidth - 2 * VIEWPORT_MARGIN);
    if (currentWidth >= maxWidthPx) {
      componentRef.instance.width = maxWidthPx / REM;
    } else {
      componentRef.instance.width = null;
    }
  }

  _setAutoPosition(tooltipHeight: number, parentRect: DOMRect, windowHeight: number): void {
    const { top, bottom } = parentRect;

    if (tooltipHeight > windowHeight - bottom) {
      this.position = TooltipPosition.ABOVE;
      return;
    }

    if (tooltipHeight > top) {
      this.position = TooltipPosition.BELOW;
      return;
    }

    this.position = this.tooltipPosition;
  }

  _calculateLeftOffset(tooltipWidth: number, parentRect: DOMRect, windowWidth: number): number {
    const { left, right } = parentRect;
    const center: number = (left + right) / 2;
    const halfTooltipWidth: number = tooltipWidth / 2;

    if (halfTooltipWidth + VIEWPORT_MARGIN > center) {
      return halfTooltipWidth - center + VIEWPORT_MARGIN;
    }

    if (halfTooltipWidth + VIEWPORT_MARGIN > windowWidth - center) {
      return windowWidth - center - halfTooltipWidth - VIEWPORT_MARGIN;
    }

    return 0;
  }

  _setTooltipOffsetAndPosition(): void {
    const { width, height } = this.tooltipService.tooltipComponentRef.location.nativeElement.children[0].getBoundingClientRect();
    const parentRect: DOMRect = this.elementRef.nativeElement.getBoundingClientRect();
    this.leftOffset = this._calculateLeftOffset(width, parentRect, document.documentElement.clientWidth);
    this._setAutoPosition(height, parentRect, window.innerHeight);
  }

  _setAriaAttribute(ariaType: TooltipAriaType): void {
    this.renderer.setAttribute(this.parentElement, ariaType, this._getAriaAttributeValue(ariaType));
  }

  _removeAriaAttribute(ariaType: TooltipAriaType): void {
    this.renderer.removeAttribute(this.parentElement, ariaType);
  }

  _getAriaAttributeValue(ariaType: TooltipAriaType): string {
    return ariaType === 'aria-label' ? this.tooltipText : this.tooltipId;
  }

  _getParentElement(element: HTMLElement): HTMLElement {
    if (this.interactivityChecker.isTabbable(element)) {
      return element;
    }
    return FocusUtil.getTabbableElement(element, this.interactivityChecker) ?? element;
  }

  _getTopPosition(parentTop: number, parentBottom: number, tooltipPosition: TooltipPosition): number {
    return tooltipPosition === TooltipPosition.ABOVE ? parentTop : parentBottom;
  }

  _hide(): void {
    this.tooltipService.hide(this._ownerToken);
  }

  _destroy(): void {
    this.debouncedShowTooltip?.cancel();
    this.tooltipService.hide(this._ownerToken);
    this._removeAriaAttribute(this.tooltipAriaType);
    this.parentElement = null;
    this.tooltipText = null;
    this.tooltipId = null;
  }
}
